/* eslint-disable no-debugger */
import React, {
  memo,
  useEffect,
  useReducer,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  Input,
  Spinner,
  Alert
} from "reactstrap";
import { Accordion } from "react-bootstrap";
import { AvField, AvForm } from "availity-reactstrap-validation";
import validatePositiveInputs from "helpers/validatePositiveInputs";
import { updateSharedIbAgreement } from "store/actions";
import AvFieldSelect from "components/Common/AvFieldSelect";
import { getProductCategoryLabel } from "common/productCategories";

const getAccountTypeName = (accountTypeId, accountTypes) => {
  return accountTypes?.find((type) => type._id === accountTypeId)?.title;
};

const getAccountType = (accountTypeId, accountTypes) => {
  return accountTypes?.find((type) => type._id === accountTypeId);
};

const toSafeNumber = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return 0;
  const parsed = parseFloat(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const AgreementTotal = ({ total, accountTypeName, dispatcher }) => {
  return (<Row key={total.accountTypeId} className="my-1 align-items-center">
    <Col md="3">
      {accountTypeName}
    </Col>
    <Col>
      <Input
        bsSize="sm"
        type="text"
        value={total.rebate}
        onKeyPress={(e) => validatePositiveInputs(e)}
        onChange={(e) => {
          dispatcher({
            type: "UPDATE_TOTAL_REBATE",
            payload: {
              accountTypeId: total.accountTypeId,
              rebate: e.target.value,
            },
          });
        }}
      />
    </Col>
    <Col>
      <Input
        className="mt-3"
        bsSize="sm"
        type="text"
        value={total.commission}
        onKeyPress={(e) => validatePositiveInputs(e)}
        onChange={(e) => {
          dispatcher({
            type: "UPDATE_TOTAL_COMMISSION",
            payload: {
              accountTypeId: total.accountTypeId,
              commission: e.target.value,
            },
          });
        }}
      />
    </Col>
  </Row>);
};

const InvestorForm = ({ val, valIdx, agreementName, dispatcher, member }) => {
  const { t } = useTranslation();
  return (
    <Accordion.Item key={val.accountTypeId} eventKey={valIdx}>
      <Accordion.Header>
        <Row className="justify-content-center mb-2 fw-bold gx-0">
          <Col md="12" className="text-center">
            {agreementName}
          </Col>
        </Row>
      </Accordion.Header>
      <Accordion.Body>
        <Row>
          <Col md="3" className="text-center">
            {t("PAMM Profit Share")} %
          </Col>
          <Col md="9" className="text-center">
            <Input
              type="text"
              value={val.pamProfitShare}
              onKeyPress={(e) => validatePositiveInputs(e)}
              onChange={(e) => {
                dispatcher({
                  type: "UPDATE_MEMBER_PAMM_PROFIT_SHARE",
                  payload: {
                    accountTypeId: val.accountTypeId,
                    customerId: member.customerId,
                    pamProfitShare: e.target.value,
                  },
                });
              }}
            />
          </Col>
        </Row>
      </Accordion.Body>
    </Accordion.Item>
  );
};

const AccountForm = ({ val, valIdx, agreementName, dispatcher, member, products, markups }) => {
  return (<Accordion.Item key={val.accountTypeId} eventKey={valIdx}>
    <Accordion.Header>
      <Row className="w-100 my-1 align-items-center my-0">
        <Col md="3">
          {agreementName}
        </Col>
        <Col>
          <Input
            bsSize="sm"
            type="text"
            value={val.rebate}
            onKeyPress={(e) => validatePositiveInputs(e)}
            onChange={(e) => {
              dispatcher({
                type: "UPDATE_MEMBER_REBATE_TOTAL",
                payload: {
                  accountTypeId: val.accountTypeId,
                  rebate: e.target.value,
                  customerId: member.customerId,
                },
              });
            }}
          />
        </Col>
        <Col>
          <Input
            bsSize="sm"
            type="text"
            value={val.commission}
            onKeyPress={(e) => validatePositiveInputs(e)}
            onChange={(e) => {
              dispatcher({
                type: "UPDATE_MEMBER_COMMISSION_TOTAL",
                payload: {
                  accountTypeId: val.accountTypeId,
                  commission: e.target.value,
                  customerId: member.customerId,
                },
              });
            }}
          />
        </Col>
      </Row>
    </Accordion.Header>
    <Accordion.Body>
      {Object.keys(val?.products ?? {}).map((prod, prodIdx) => (
        <Row
          key={prodIdx}
          className="my-1 align-items-center"
        >
          <Col md="3">{getProductCategoryLabel(
            products.find((p) => p?.toLowerCase() === prod?.toLowerCase()) || prod
          )}</Col>
          <Col>
            <Input
              bsSize="sm"
              type="text"
              value={val?.products?.[prod]?.rebate ?? 0}
              onKeyPress={(e) => validatePositiveInputs(e)}
              onChange={(e) => {
                dispatcher({
                  type: "UPDATE_MEMBER_REBATE_PRODUCT",
                  payload: {
                    accountTypeId: val.accountTypeId,
                    rebate: e.target.value,
                    customerId: member.customerId,
                    product: prod,
                  },
                });
              }}
            />
          </Col>
          <Col>
            <Input
              bsSize="sm"
              type="text"
              value={val?.products?.[prod]?.commission ?? 0}
              onKeyPress={(e) => validatePositiveInputs(e)}
              onChange={(e) => {
                dispatcher({
                  type: "UPDATE_MEMBER_COMMISSION_PRODUCT",
                  payload: {
                    accountTypeId: val.accountTypeId,
                    commission: e.target.value,
                    customerId: member.customerId,
                    product: prod,
                  },
                });
              }}
            />
          </Col>
        </Row>
      ))}
      <Row>
        <Col md="3">Markup</Col>
        <Col>
          <AvFieldSelect
            name={`members[${member.customerId}].values[${valIdx}].markup`}
            value={val.markup}
            onChange={(markup) => {
              dispatcher({
                type: "UPDATE_MEMBER_MARKUP",
                payload: {
                  accountTypeId: val.accountTypeId,
                  customerId: member.customerId,
                  markup,
                },
              });
            }}
            options={(markups || []).map((obj) => {
              return ({
                label: `${obj}`,
                value: obj
              });
            })}
          />
        </Col>
      </Row>
    </Accordion.Body>
  </Accordion.Item>);
};

const MemoizedAgreementTotal = memo(AgreementTotal);
const MemoizedAccountsForm = memo(AccountForm);
const MemoizedInvestorForm = memo(InvestorForm);

const buildInitialPayload = (agreement, accountTypes, products) => ({
  title: agreement?.title ?? "",
  totals: (agreement?.totals || []).map((total) => ({
    accountTypeId: total.accountTypeId,
    rebate: total.rebate ?? 0,
    commission: total.commission ?? 0,
  })),
  members: (agreement?.members || []).map((member) => ({
    customerId: member.customerId._id,
    level: member.level,
    values: (member.values || []).map((val) => {
      const accountType = getAccountType(val.accountTypeId, accountTypes);
      const isInvestor = accountType?.type === "INVESTOR";
      const base = {
        accountTypeId: val.accountTypeId,
        rebate: val.rebate ?? 0,
        commission: val.commission ?? 0,
        markup: val.markup ?? "",
        products: (products || []).reduce((acc, prod) => {
          acc[prod] = {
            rebate: val?.products?.[prod]?.rebate ?? 0,
            commission: val?.products?.[prod]?.commission ?? 0,
          };
          return acc;
        }, {}),
      };
      if (isInvestor) {
        base.pamProfitShare = val.pamProfitShare ?? 0;
      }
      return base;
    }),
  })),
});

const EditSharedIbModal = ({
  show,
  toggle: toggleModal,
  accountTypes,
  products,
  agreement,
  markups,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [submissionFailure, setSubmissionFailure] = useState(false);
  const [error, setError] = useState(null);

  const { submitting, clearingCounter, error: submitError } = useSelector((state) => state.ibAgreements);

  const [payload, dispatcher] = useReducer((state, action) => {
    switch (action.type) {
      case "INIT":
        return action.payload;
      case "UPDATE_TITLE":
        return {
          ...state,
          title: action.payload 
        };
      case "UPDATE_TOTAL_REBATE":
        return {
          ...state,
          totals: state.totals.map((total) => total.accountTypeId === action.payload.accountTypeId
            ? {
              ...total,
              rebate: action.payload.rebate 
            }
            : total),
        };
      case "UPDATE_TOTAL_COMMISSION":
        return {
          ...state,
          totals: state.totals.map((total) => total.accountTypeId === action.payload.accountTypeId
            ? {
              ...total,
              commission: action.payload.commission 
            }
            : total),
        };
      case "UPDATE_MEMBER_REBATE_TOTAL":
        return {
          ...state,
          members: state.members.map((member) => {
            if (member.customerId !== action.payload.customerId) return member;
            return {
              ...member,
              values: member.values.map((val) => val.accountTypeId === action.payload.accountTypeId
                ? {
                  ...val,
                  rebate: action.payload.rebate 
                }
                : val),
            };
          }),
        };
      case "UPDATE_MEMBER_COMMISSION_TOTAL":
        return {
          ...state,
          members: state.members.map((member) => {
            if (member.customerId !== action.payload.customerId) return member;
            return {
              ...member,
              values: member.values.map((val) => val.accountTypeId === action.payload.accountTypeId
                ? {
                  ...val,
                  commission: action.payload.commission 
                }
                : val),
            };
          }),
        };
      case "UPDATE_MEMBER_REBATE_PRODUCT":
        return {
          ...state,
          members: state.members.map((member) => {
            if (member.customerId !== action.payload.customerId) return member;
            return {
              ...member,
              values: member.values.map((val) => {
                if (val.accountTypeId !== action.payload.accountTypeId) return val;
                return {
                  ...val,
                  products: {
                    ...val.products,
                    [action.payload.product]: {
                      ...val.products?.[action.payload.product],
                      rebate: action.payload.rebate,
                    },
                  },
                };
              }),
            };
          }),
        };
      case "UPDATE_MEMBER_COMMISSION_PRODUCT":
        return {
          ...state,
          members: state.members.map((member) => {
            if (member.customerId !== action.payload.customerId) return member;
            return {
              ...member,
              values: member.values.map((val) => {
                if (val.accountTypeId !== action.payload.accountTypeId) return val;
                return {
                  ...val,
                  products: {
                    ...val.products,
                    [action.payload.product]: {
                      ...val.products?.[action.payload.product],
                      commission: action.payload.commission,
                    },
                  },
                };
              }),
            };
          }),
        };
      case "UPDATE_MEMBER_PAMM_PROFIT_SHARE":
        return {
          ...state,
          members: state.members.map((member) => {
            if (member.customerId !== action.payload.customerId) return member;
            return {
              ...member,
              values: member.values.map((val) => val.accountTypeId === action.payload.accountTypeId
                ? {
                  ...val,
                  pamProfitShare: action.payload.pamProfitShare 
                }
                : val),
            };
          }),
        };
      case "UPDATE_MEMBER_MARKUP":
        return {
          ...state,
          members: state.members.map((member) => {
            if (member.customerId !== action.payload.customerId) return member;
            return {
              ...member,
              values: member.values.map((val) => val.accountTypeId === action.payload.accountTypeId
                ? {
                  ...val,
                  markup: action.payload.markup 
                }
                : val),
            };
          }),
        };
      default:
        return state;
    }
  }, {
    title: "",
    totals: [],
    members: [] 
  });

  useEffect(() => {
    if (clearingCounter > 0 && show) {
      toggle();
    }
  }, [clearingCounter]);

  useEffect(() => {
    setSubmissionFailure(false);
  }, []);

  useEffect(() => {
    if (agreement && show) {
      dispatcher({
        type: "INIT",
        payload: buildInitialPayload(agreement, accountTypes, products),
      });
    }
  }, [agreement, show]);

  const { title, totals, members } = payload;

  const validateSharedAgreement = (params) => {
    const {
      title: agreementTitle, totals: agreementTotals, members: agreementMembers
    } = params;
    if (!agreementTitle || !agreementTotals || !agreementMembers) {
      return setError("Invalid values found. Please check the form again!");
    }
    const totalsMap = new Map();
    for (let i = 0; i < agreementTotals.length; i++) {
      const total = agreementTotals[i];
      totalsMap.set(total.accountTypeId, {
        rebate: total.rebate || 0,
        commission: total.commission || 0,
      });
    }
    const membersMap = new Map();
    for (let i = 0; i < agreementMembers.length; i++) {
      const member = agreementMembers[i];
      let isMemberWiseTotalValid = true;
      for (let j = 0; j < member.values.length; j++) {
        const value = member.values[j];
        const key = value.accountTypeId;
        const rebate = value.rebate || 0;
        const commission = value.commission || 0;
        const productsValue = value.products;
        // note that products is an object. So, we need to iterate over it and verify that sum of all products is equal to the rebate and commission
        let maxRebate = 0;
        let maxCommission = 0;
        for (const prod in productsValue) {
          const currentRebate = productsValue[prod].rebate || 0;
          const currentCommission = productsValue[prod].commission || 0;
          if (currentRebate > maxRebate) {
            maxRebate = currentRebate;
          }
          if (currentCommission > maxCommission) {
            maxCommission = currentCommission;
          }
        }
        if (maxRebate > rebate || maxCommission > commission) {
          isMemberWiseTotalValid = false;
          setError("Products sum is invalid.");
          break;
        }
        membersMap.set(key, {
          rebate: (membersMap.get(key)?.rebate || 0) + rebate,
          commission: (membersMap.get(key)?.commission || 0) + commission,
        });
        if (
          totalsMap.get(key)?.rebate < membersMap.get(key)?.rebate ||
          totalsMap.get(key)?.commission < membersMap.get(key)?.commission
        ) {
          isMemberWiseTotalValid = false;
          setError("Member wise total is invalid.");
          break;
        }
      }
      if (!isMemberWiseTotalValid) {
        return;
      }
    }
    setError(null);
    dispatch(
      updateSharedIbAgreement({
        id: agreement._id,
        title: agreementTitle,
        totals: agreementTotals,
        members: agreementMembers,
      })
    );
  };

  // Values are stored as raw strings during typing to preserve intermediate
  // states like "0." — convert them to numbers here at submit time.
  const buildRequestPayload = () => ({
    title,
    totals: totals.map((total) => ({
      accountTypeId: total.accountTypeId,
      rebate: toSafeNumber(total.rebate),
      commission: toSafeNumber(total.commission),
    })),
    members: members.map((member) => ({
      customerId: member.customerId,
      level: member.level,
      values: member.values.map((value) => {
        const accountType = getAccountType(value.accountTypeId, accountTypes);
        if (accountType?.type === "INVESTOR") {
          return {
            accountTypeId: value.accountTypeId,
            pamProfitShare: toSafeNumber(value.pamProfitShare),
          };
        }
        return {
          accountTypeId: value.accountTypeId,
          rebate: toSafeNumber(value.rebate),
          commission: toSafeNumber(value.commission),
          markup: value.markup,
          products: value.products
            ? Object.fromEntries(
              Object.entries(value.products).map(([prod, pv]) => [
                prod,
                {
                  rebate: toSafeNumber(pv.rebate),
                  commission: toSafeNumber(pv.commission),
                },
              ])
            )
            : value.products,
        };
      }),
    })),
  });

  const toggle = () => {
    setSubmissionFailure(false);
    setError(null);
    toggleModal();
  };

  return (
    <Modal isOpen={show} toggle={toggle} centered={true} scrollable={true} onClosed={() => {
      setSubmissionFailure(false);
      setError(null);
    }}>
      <ModalHeader toggle={toggle} tag="h4">
        {t("Edit Shared IB Agreement")}
      </ModalHeader>
      <ModalBody>
        {error && (
          <div
            style={{
              display: "block",
              color: "white",
              marginBottom: 10,
              padding: 10,
              borderRadius: 5,
              backgroundColor: "#f65854"
            }}
            className="formValidationErrMessage"
          >
            {error}
          </div>
        )}
        <AvForm
          validationEvent={["onChange", "onInput", "onBlur"]}
          onValidSubmit={() => {
            setSubmissionFailure(false);
            validateSharedAgreement(buildRequestPayload());
          }}
          onInvalidSubmit={() => {
            setSubmissionFailure(true);
          }}
        >
          <AvField
            name={"title"}
            value={title}
            label={t("Agreement name")}
            className="mb-3"
            type="text"
            errorMessage={t("Required!")}
            validate={{ required: { value: true } }}
            onChange={(e) => {
              dispatcher({
                type: "UPDATE_TITLE",
                payload: e.target.value,
              });
            }}
          />
          <Row className="justify-content-start">
            <Col md="3">{t("Type")}</Col>
            <Col>{t("Total Rebate")}</Col>
            <Col>{t("Total Commission")}</Col>
          </Row>
          {totals?.map((total, totalIdx) => (
            <MemoizedAgreementTotal key={totalIdx} total={total} accountTypeName={
              getAccountTypeName(total.accountTypeId, accountTypes)
            } dispatcher={dispatcher} />
          ))}
          <hr className="my-3" />
          <Accordion className="mb-3" alwaysOpen>
            {members
              ?.slice()
              .sort((a, b) => (a.level > b.level ? 1 : -1))
              .map((member, memberIdx) => {
                const originalMember = agreement?.members?.find((m) => m.customerId._id === member.customerId);
                return (
                  <Accordion.Item key={member.customerId} eventKey={memberIdx}>
                    <Accordion.Header>
                      {originalMember?.customerId?.firstName} {originalMember?.customerId?.lastName}
                    </Accordion.Header>
                    <Accordion.Body>
                      <Accordion className="my-1" alwaysOpen>
                        {member?.values?.map((val, valIdx) => (
                          (getAccountType(val.accountTypeId, accountTypes)?.type === "INVESTOR") ?
                            <MemoizedInvestorForm key={val.accountTypeId} val={val} valIdx={valIdx} agreementName={getAccountTypeName(val.accountTypeId, accountTypes)} dispatcher={dispatcher} member={member} />
                            : (
                              <MemoizedAccountsForm
                                key={val.accountTypeId}
                                val={val}
                                valIdx={valIdx}
                                agreementName={getAccountTypeName(val.accountTypeId, accountTypes)}
                                dispatcher={dispatcher}
                                member={member}
                                products={products}
                                markups={markups}
                              />
                            )
                        ))}
                      </Accordion>
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
          </Accordion>
          {!submitting && submitError && (
            <Alert color="danger" className="mt-3">{submitError}</Alert>
          )}
          <div className="d-flex align-items-center gap-2 mt-3">
            <Button type="submit" disabled={submitting}>
              {t("Submit")}
            </Button>
            {submitting && (
              <span>
                <Spinner size="sm" color="primary" /> {t("Submitting...")}
              </span>
            )}
          </div>
        </AvForm>
      </ModalBody>
    </Modal>
  );
};

export default memo(EditSharedIbModal);
